import axios, { AxiosError, AxiosResponse } from 'axios';
import { baseUrlFactory } from './apiCommons';

interface DataOrError<Data> {
  data?: Data;
  error?: AxiosError;
  res?: AxiosResponse<Data>;
}

export class Api {
  static api = axios.create({
    baseURL:
      baseUrlFactory('api', import.meta.env.VITE_APP_PROD_URL as string) +
      '/api',
  });

  static createAPIEndpoint() {
    return (
      baseUrlFactory('api', import.meta.env.VITE_APP_PROD_URL as string) +
      '/api'
    );
  }

  static logout() {
    this.api = axios.create({
      baseURL: this.createAPIEndpoint(),
    });
    axios.defaults.baseURL = this.createAPIEndpoint();
    axios.defaults.headers.common.Authorization = undefined;
    axios.defaults.withCredentials = true;
    return this;
  }

  static authenticate(token: string) {
    if (!token) {
      return this.logout();
    }
    const headers = {
      Authorization: `Bearer ${token}`,
    };
    this.api = axios.create({
      baseURL: this.createAPIEndpoint(),
      headers,
    });
    axios.defaults.baseURL = this.createAPIEndpoint();
    axios.defaults.headers.common.Authorization = headers.Authorization;
    axios.defaults.withCredentials = true;

    return this;
  }

  static async get<Data>(url: string): Promise<DataOrError<Data>> {
    try {
      const res = await this.api.get<Data>(url);
      return { data: res.data, res };
    } catch (error) {
      return { error: error as AxiosError };
    }
  }

  static async post<Data, Parameters>(
    url: string,
    params: Parameters,
  ): Promise<DataOrError<Data>> {
    try {
      const res = await this.api.post<Data>(url, params);
      return { data: res.data, res };
    } catch (error) {
      return { error: error as AxiosError };
    }
  }

  static async patch<Data, Parameters>(
    url: string,
    params: Parameters,
  ): Promise<DataOrError<Data>> {
    try {
      const res = await this.api.patch(url, params);
      return { data: res.data as Data, res };
    } catch (error) {
      return { error: error as AxiosError };
    }
  }

  static async delete<Data>(url: string): Promise<DataOrError<Data>> {
    try {
      const res = await this.api.delete(url);
      return { data: res.data as Data, res };
    } catch (error) {
      return { error: error as AxiosError };
    }
  }

  static async put<Data, Parameters>(
    url: string,
    params: Parameters,
  ): Promise<DataOrError<Data>> {
    try {
      const res = await this.api.put(url, params);
      return { data: res.data as Data, res };
    } catch (error) {
      return { error: error as AxiosError };
    }
  }
}
